import React, { useEffect, useState } from "react";
import { ordersApi } from "../services/orders";
import type { HistoryItem } from "../types/orders";
import { formatTxnAmount } from "../utils/currency";
import SetlistArt from "./SetlistArt";
import "./RecentRequestsBubble.css";
import { formatWhen } from "../utils/formatWhen";
import { t } from "../i18n";
import { useAccountCache } from "../hooks/useAccountCache";

// Recent-activity bubble at the foot of the Requests page (founder brief,
// modelled on Revolut's home-screen transaction card).
//
// Why it exists: below the Pending stack the page ran out of content and went
// black. This card always occupies that space — three most recent handled
// requests, newest first, and a "See all" that opens the full history. A
// brand-new talent with no history gets a friendly placeholder instead of the
// void, so the screen is never empty.
//
// Data: the SAME two endpoints the history modal reads
// (/orders/played + /orders/declined) — no new server route. Both are
// talent-scoped and already exclude tips.

interface Props {
  /** Opens the full Request History modal. */
  onSeeAll: () => void;
  /** Bump to refetch — the page passes a counter that changes on play/decline. */
  refreshKey?: number;
  /** Handled DEMO practice cards as rows (lib/demoRequest demoHistoryItems).
      The demos are NEVER Orders, so the /orders endpoints can't return them —
      they are injected here (same convention as the history modal). For a
      brand-new talent they're the FIRST thing the bubble shows; real
      requests then layer on top and they roll off. */
  demoItems?: HistoryItem[];
}

const ROWS = 3;

const RecentRequestsBubble: React.FC<Props> = ({
  onSeeAll,
  refreshKey = 0,
  demoItems = [],
}) => {
  // The last three rows paint at once on a later visit; the skeleton is for
  // the very first open only.
  const [items, setItems, fromCache] = useAccountCache<HistoryItem[]>("reqs-recent", []);
  const [loading, setLoading] = useState(!fromCache);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        // includeRefunded keeps a just-refunded request visible here as a
        // REFUNDED row instead of it silently vanishing from "recent
        // activity" (an older server ignores the param and omits them).
        const [p, d] = await Promise.all([
          ordersApi.played({ includeRefunded: true }),
          ordersApi.declined(),
        ]);
        if (!alive) return;
        // Both endpoints come back newest-first on their own; merged they need
        // one more sort. updatedAt is when the request was HANDLED (played or
        // declined) — createdAt would order by when the fan asked.
        // Failed-capture plays are excluded here exactly like the history
        // modal — the two lists must never disagree about what happened.
        const merged = [...(p || []), ...(d || [])]
          .filter((o) => !o.captureFailed)
          .sort(
            (a, b) =>
              new Date(b.updatedAt || b.createdAt).getTime() -
              new Date(a.updatedAt || a.createdAt).getTime()
          );
        setItems(merged.slice(0, ROWS));
      } catch {
        // A failed fetch shows the placeholder, never an error card — this is
        // ambient content, not something the talent asked for.
        if (alive) setItems([]);
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => {
      alive = false;
    };
  }, [refreshKey, setItems]);

  if (loading) {
    return (
      <section className="rrb-card" aria-busy="true">
        <div className="rrb-skeleton-row" />
        <div className="rrb-skeleton-row" />
        <div className="rrb-skeleton-row" />
      </section>
    );
  }

  // Demo rows merge by their real handledAt with the real ones — for a
  // brand-new talent they ARE the list; three real requests later they have
  // rolled off.
  const rows = demoItems.length
    ? [...items, ...demoItems]
        .sort(
          (a, b) =>
            new Date(b.updatedAt || b.createdAt).getTime() -
            new Date(a.updatedAt || a.createdAt).getTime()
        )
        .slice(0, ROWS)
    : items;

  return (
    <section className="rrb-card" aria-label={t("recentRequestsBubble.recentRequests")}>
      {rows.length === 0 ? (
        <div className="rrb-empty">
          <div className="rrb-empty-title">{t("recentRequestsBubble.noRequestsYet")}</div>
          <div className="rrb-empty-sub">
            {t("recentRequestsBubble.songsYouPlayOr")}
          </div>
        </div>
      ) : (
        <>
          <ul className="rrb-list">
            {rows.map((i) => {
              const played = i.status === "played";
              const refunded = i.status === "refunded";
              // Only a still-declined order is "awaiting the counter". Once the
              // fan pays the counter the order flips back through pending →
              // played and KEEPS declineReason, so the played branch must not
              // read that field or every counter-paid row is mislabelled.
              const countered =
                i.status === "declined" &&
                i.declineReason === "more_money" &&
                (i.counterAmount ?? 0) > 0;
              return (
                <li className="rrb-row" key={i._id}>
                  <div className="rrb-avatar-wrap">
                    {i.isPlaylist ? (
                      <span className="rrb-avatar rrb-avatar--playlist" aria-hidden>
                        🎚️
                      </span>
                    ) : i.trackImages && i.trackImages.length ? (
                      <SetlistArt images={i.trackImages} className="rrb-avatar" />
                    ) : (
                      <img
                        className="rrb-avatar"
                        src={i.trackImage || "/fallback.png"}
                        alt=""
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).src = "/fallback.png";
                        }}
                      />
                    )}
                    <span
                      className={`rrb-badge ${
                        refunded
                          ? "rrb-badge--refunded"
                          : played
                          ? "rrb-badge--played"
                          : "rrb-badge--declined"
                      }`}
                      aria-hidden="true"
                    >
                      {refunded ? "↩" : played ? "♪" : "✕"}
                    </span>
                  </div>

                  <div className="rrb-mid">
                    <div className="rrb-title">
                      {i.searchQuery}
                      {i.isDemo && <span className="rrb-demo-chip">{t("recentRequestsBubble.demo")}</span>}
                    </div>
                    <div className="rrb-sub">{formatWhen(i.updatedAt || i.createdAt)}</div>
                  </div>

                  {/* Same right column as the history rows — status label over
                      amount — so opening "See all" is zero visual shock. */}
                  <div className="rrb-right">
                    {refunded ? (
                      <div className="rrb-refunded-chip">{t("recentRequestsBubble.refunded")}</div>
                    ) : (
                      <div className={`rrb-status ${countered ? "rrb-status--countered" : ""}`}>
                        {countered
                          ? t("recentRequestsBubble.countered")
                          : played
                          ? i.isPlaylist
                            ? t("recentRequestsBubble.delivered")
                            : t("recentRequestsBubble.played")
                          : t("recentRequestsBubble.declined")}
                      </div>
                    )}
                    <div
                      className={`rrb-amount ${
                        i.isDemo
                          ? "rrb-amount--demo"
                          : refunded
                          ? "rrb-amount--refunded"
                          : played
                          ? "rrb-amount--in"
                          : "rrb-amount--out"
                      }`}
                    >
                      {played ? "+" : ""}
                      {formatTxnAmount(Number(i.amount) || 0, i.currency)}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>

          <button type="button" className="rrb-see-all" onClick={onSeeAll}>
            {t("recentRequestsBubble.seeAll")}
          </button>
        </>
      )}
    </section>
  );
};

export default RecentRequestsBubble;
